import { ListRange } from '@angular/cdk/collections';
import {
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLL_STRATEGY,
  VirtualScrollStrategy,
} from '@angular/cdk/scrolling';
import { Directive, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { distinctUntilChanged, Observable, Subject } from 'rxjs';

// Custom VirtualScrollStrategy for the chat message list.
//
// Replaces `@angular/cdk-experimental`'s autosize strategy, which estimates
// total content size from sampled rows and visibly mis-anchors / jitters as
// the estimate refines. This strategy maintains an explicit per-row height
// cache fed by a `ResizeObserver` in the host component; total size, rendered
// range, and scroll-to-index are all computed from real measurements.
//
// Anatomy:
//   - `_heights`: id -> measured border-box height (incl. padding/border).
//   - `_offsets`: prefix-sum array. `_offsets[i]` is the y-offset (from
//     content start) of item `i`. `_offsets[len]` is the total content size.
//     Built lazily; invalidated on data-length change or any height update.
//   - `_itemIdProvider`: lets the strategy look up the stable id for an
//     index, so the cache survives data array rebuilds (room change, sort,
//     prune-from-end).
//   - Item ids whose height has not yet been measured fall back to
//     `_defaultHeight`. The first render after attach uses that estimate
//     everywhere; the ResizeObserver then fires per row and the strategy
//     re-renders with real heights.
//
// Why not just measure inside the strategy? The strategy has no Angular
// lifecycle and no clean hook to grab the rendered DOM elements after
// `setRenderedRange`. The host component knows when Angular has rendered
// and owns the `cdk-virtual-scroll-viewport`'s element subtree, so it does
// the DOM walking and feeds heights back via `setHeight`. This keeps the
// strategy free of DOM-querying responsibility.

export interface ItemIdProvider {
  itemIdAt(index: number): string | undefined;
}

// Returns the strategy's fallback height for a row whose real height
// hasn't been measured yet. Set by the host via `setHeightEstimator`.
// When unset, the strategy falls back to `_defaultHeight` for every
// unmeasured row — that's the legacy "every unknown row is 80 px" mode,
// fine when rows are small and uniform but a major source of cascade
// adjustment when they aren't.
export type RowHeightEstimator = (index: number) => number;

export class MessageListScrollStrategy implements VirtualScrollStrategy {
  scrolledIndexChange: Observable<number>;

  private _viewport: CdkVirtualScrollViewport | null = null;
  private _heights = new Map<string, number>();
  private _itemIdProvider: ItemIdProvider | null = null;
  private _heightEstimator: RowHeightEstimator | null = null;
  private _scrolledIndexChange$ = new Subject<number>();

  // Prefix-sum cache: `_offsets[i]` = y-offset of row `i`. Always one entry
  // longer than the data; the last entry is the total content size.
  private _offsets: number[] | null = null;

  // Scroll-anchor state — see `_withScrollAnchor`. The anchor identifies a
  // specific row + the user's screen position within it, and persists
  // across the entire measurement cascade that follows a scroll. Resets on
  // user-initiated scrolls (detected as a divergence between the viewport's
  // scrollOffset and `_lastWrittenScroll`).
  private _anchor: {
    rowIndex: number;
    relOffsetInRow: number;
    // Where on screen (px from viewport top) the anchor row was when first
    // captured. Used to back-compute scrollTop = anchorContentPos - anchorScreenOffset
    // each cascade round so the row stays at the same y on screen.
    anchorScreenOffset: number;
  } | null = null;

  // The scroll offset the strategy itself last wrote (bottom pin, anchor
  // restore, scrollToIndex). Nulled by `onContentScrolled` once the actual
  // position diverges from it — that's the "the user owns the position now"
  // signal, used both to invalidate the anchor and to pick the at-bottom
  // band in `_withScrollAnchor`.
  private _lastWrittenScroll: number | null = null;

  constructor(
    private _defaultHeight: number,
    private _bufferPx: number
  ) {
    this.scrolledIndexChange = this._scrolledIndexChange$.pipe(distinctUntilChanged());
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this._viewport = viewport;
    this._invalidateOffsets();
    this._update();
  }

  detach(): void {
    this._scrolledIndexChange$.complete();
    this._viewport = null;
    this._anchor = null;
    this._lastWrittenScroll = null;
  }

  // Host-facing API ---------------------------------------------------------

  setItemIdProvider(provider: ItemIdProvider): void {
    this._itemIdProvider = provider;
    this._invalidateOffsets();
    this._update();
  }

  // Per-row fallback for unmeasured heights. Should be a fast, side-
  // effect-free function (called during offset rebuilds, potentially for
  // every row in the data array). When set, replaces `_defaultHeight` for
  // any row that hasn't been measured yet.
  setHeightEstimator(estimator: RowHeightEstimator | null): void {
    this._heightEstimator = estimator;
    this._invalidateOffsets();
    this._update();
  }

  // Triggers a full prefix-sum rebuild + render. The host calls this after
  // a change that affects the estimator's output (e.g. viewport width or
  // chat layout switched) so the strategy picks up the new estimates.
  invalidateEstimates(): void {
    this._invalidateOffsets();
    this._update();
  }

  // Called by the host's ResizeObserver. Returns true if the cached value
  // changed (host can use this to decide whether to nudge for a re-render).
  setHeight(id: string, height: number): boolean {
    // Round to avoid sub-pixel ResizeObserver chatter that would invalidate
    // the offset cache and force unnecessary re-renders.
    const rounded = Math.round(height);
    const prev = this._heights.get(id);
    if (prev === rounded) return false;
    this._withScrollAnchor(() => {
      this._heights.set(id, rounded);
      this._invalidateOffsets();
    });
    return true;
  }

  // Batched variant of `setHeight`. A ResizeObserver callback delivers a
  // synchronous batch of entries; folding the whole batch into a single
  // invalidate + update saves O(N²) work on cold load (N rows each
  // triggering a full prefix-sum rebuild).
  setHeights(entries: Iterable<readonly [id: string, height: number]>): void {
    const updates: Array<readonly [string, number]> = [];
    for (const [id, height] of entries) {
      const rounded = Math.round(height);
      const prev = this._heights.get(id);
      if (prev === rounded) continue;
      updates.push([id, rounded]);
    }
    if (updates.length === 0) return;
    this._withScrollAnchor(() => {
      for (const [id, h] of updates) this._heights.set(id, h);
      this._invalidateOffsets();
    });
  }

  clearHeights(): void {
    this._heights.clear();
    this._anchor = null;
    this._lastWrittenScroll = null;
    this._invalidateOffsets();
    this._update();
  }

  // Drops cached heights for ids no longer present anywhere in the message
  // store (retention prune, deletion). Ids never come back once removed —
  // messages keep their UUID for life — so the prefix-sum can't reference a
  // pruned entry again and no offset invalidation or re-render is needed.
  // Keeps the cache from growing for the lifetime of the session. `keep`
  // must span ALL rooms, not just the one on screen, or switching rooms
  // would throw away warm measurements.
  pruneHeights(keep: ReadonlySet<string>): void {
    for (const id of this._heights.keys()) {
      if (!keep.has(id)) this._heights.delete(id);
    }
  }

  // Pins one specific row's screen position across the entire measurement
  // cascade that follows a scroll into unmeasured territory. Without
  // anchoring, ResizeObserver measurements rebuild the offset table while
  // scrollTop sits still — every row whose content position drifted up or
  // down also drifts on screen, so the rows the user is reading slide
  // around (often hundreds of pixels) before the cascade settles.
  //
  // The anchor is captured *once* per cascade, not once per setHeights call.
  // Re-picking each round chases the cascade: the chosen row's identity
  // changes as scrollTop adjusts, and the user-visible focus row leaks out
  // of view by the end. Persisting the anchor — and treating "user
  // scrolled" as the invalidation signal — keeps one specific row glued
  // to one specific screen position until the heights settle.
  //
  // Why the *center* of the viewport as the anchor? Top or bottom anchors
  // only stabilize rows on one side of themselves — height changes between
  // the anchor and the user's focus row still drag that row around the
  // screen. Anchoring on the row at viewport center is a heuristic for the
  // user's focus point and distributes any residual shift symmetrically
  // above and below (so no single row moves by the full cascade delta).
  //
  // scrollToOffset's effect on scrollTop is synchronous (the scroll *event*
  // fires later, but the property reads back the new value immediately), so
  // calling `_updateRenderedRange()` after the adjustment renders against
  // the corrected offset and avoids a one-frame flicker of the pre-anchor
  // range. `_lastWrittenScroll` records what we just wrote so
  // `onContentScrolled` can tell our own programmatic scroll apart from a
  // user scroll.

  // Distance from the content's bottom at which the viewport is treated as
  // "at bottom" for the live-tail special case — used while the current
  // position is one the strategy wrote itself. Generous enough to absorb a
  // few rows of estimate-vs-actual height drift across a measurement
  // cascade while still excluding anyone deliberately scrolled up reading
  // history.
  private static readonly _AT_BOTTOM_PX = 64;

  // Tight variant used when the current position came from a user scroll.
  // A deliberate wheel tick away from the bottom is ~50+ px; treating such
  // a position as "still at bottom" would re-pin the user to the bottom on
  // the next height update and rubber-band their gesture. Kept above 0 for
  // sub-pixel/rounding slack.
  private static readonly _AT_BOTTOM_USER_PX = 8;

  private _withScrollAnchor(apply: () => void): void {
    const viewport = this._viewport;
    if (!viewport) {
      apply();
      this._update();
      return;
    }
    this._ensureOffsets();
    const scrollOffset = viewport.measureScrollOffset();
    const viewportSize = viewport.getViewportSize();
    const oldTotal = this._offsets![this._offsets!.length - 1];

    // Live-tail special case: when the viewport is flush with (or very near)
    // the content's bottom, the user is following the latest messages — not
    // reading history. Anchoring a row in that mode fights with both the
    // host's bottom-stick loop (`_scrollToBottomInstant`) and the natural
    // behavior the user expects ("new messages should push me down with
    // them"). Skip the anchor entirely and re-pin to bottom after the
    // height update lands.
    //
    // The band is provenance-aware: a position the strategy wrote itself
    // (bottom pin, anchor restore) gets the generous band, because cascade
    // drift can move the content under a pinned position without any user
    // intent. A user-placed position gets the tight band — someone who just
    // wheeled ~50 px up from the bottom is leaving the tail, and re-pinning
    // them on the next height update would rubber-band the gesture.
    const ownPosition =
      this._lastWrittenScroll !== null && Math.abs(scrollOffset - this._lastWrittenScroll) <= 1;
    const atBottomBand = ownPosition
      ? MessageListScrollStrategy._AT_BOTTOM_PX
      : MessageListScrollStrategy._AT_BOTTOM_USER_PX;
    const wasAtBottom = scrollOffset + viewportSize >= oldTotal - atBottomBand;

    // Capture a fresh anchor only if we don't already have one (first
    // setHeights of the cascade, or the previous one was invalidated by a
    // user scroll). Skip capture entirely when at bottom — see above.
    //
    // Anchor at the *center* of the viewport. Top or bottom anchors only
    // stabilize rows on one side of themselves — height changes above the
    // anchor are absorbed by the scroll adjustment, but height changes
    // between the anchor and the user's focus row still drag that row
    // around. Center is a heuristic best-guess for the user's focus and
    // distributes any residual shift symmetrically above and below.
    if (!wasAtBottom && this._anchor === null) {
      const anchorScrollPos = scrollOffset + Math.floor(viewportSize / 2);
      const anchorIndex = this._indexForOffset(anchorScrollPos);
      const anchorRelOffset = anchorScrollPos - this._offsetForIndex(anchorIndex);
      this._anchor = {
        rowIndex: anchorIndex,
        relOffsetInRow: anchorRelOffset,
        anchorScreenOffset: Math.floor(viewportSize / 2),
      };
    } else if (wasAtBottom) {
      this._anchor = null;
    }

    apply();
    this._ensureOffsets();
    const newTotal = this._offsets![this._offsets!.length - 1];
    viewport.setTotalContentSize(newTotal);

    if (wasAtBottom) {
      const target = Math.max(0, newTotal - viewportSize);
      if (Math.abs(target - viewport.measureScrollOffset()) > 0.5) {
        viewport.scrollToOffset(target, 'auto');
      }
      this._lastWrittenScroll = target;
    } else if (this._anchor !== null) {
      // Clamp the relative offset so a row that *shrank* across the update
      // doesn't push the anchor past its new bottom.
      const newAnchorHeight = this._heightFor(this._anchor.rowIndex);
      const clampedRel = Math.min(this._anchor.relOffsetInRow, Math.max(0, newAnchorHeight));
      // Anchor row's NEW content position. scrollTop must be set so that
      // the anchor row sits at its original screen offset:
      //   anchorContentPos - scrollTop = anchorScreenOffset
      // → scrollTop = anchorContentPos - anchorScreenOffset.
      const anchorContentPos = this._offsetForIndex(this._anchor.rowIndex) + clampedRel;
      const targetScroll = anchorContentPos - this._anchor.anchorScreenOffset;
      const newScrollOffset = Math.max(0, Math.min(newTotal - viewportSize, targetScroll));

      if (Math.abs(newScrollOffset - viewport.measureScrollOffset()) > 0.5) {
        viewport.scrollToOffset(newScrollOffset, 'auto');
      }
      this._lastWrittenScroll = newScrollOffset;
    }
    this._updateRenderedRange();
  }

  // The two configuration knobs are also exposed via the directive's @Inputs;
  // those setters wire through here so template-bound values can override the
  // construction-time defaults (Angular applies @Input bindings *after* the
  // class-field initializer that builds the strategy).
  setDefaultHeight(height: number): void {
    if (this._defaultHeight === height) return;
    this._defaultHeight = height;
    this._invalidateOffsets();
    this._update();
  }

  setBufferPx(px: number): void {
    if (this._bufferPx === px) return;
    this._bufferPx = px;
    this._updateRenderedRange();
  }

  // VirtualScrollStrategy ---------------------------------------------------

  onContentScrolled(): void {
    // If the new scroll position differs from the value we last wrote, this
    // is a user scroll — the position is user-owned now. Invalidate the
    // current anchor (the next setHeights captures a fresh one against the
    // new viewport content) and drop the write marker so `_withScrollAnchor`
    // switches to the tight at-bottom band. A small tolerance covers
    // sub-pixel browser rounding on the scroll position.
    if (this._lastWrittenScroll !== null && this._viewport) {
      const current = this._viewport.measureScrollOffset();
      if (Math.abs(current - this._lastWrittenScroll) > 1) {
        this._lastWrittenScroll = null;
        this._anchor = null;
      }
    }
    this._updateRenderedRange();
    this._emitScrolledIndex();
  }

  onDataLengthChanged(): void {
    // Item identities may have shifted; the cached prefix-sum is stale.
    this._invalidateOffsets();
    this._update();
  }

  onContentRendered(): void {
    // Heights flow in via the host's ResizeObserver; nothing to do here.
  }

  onRenderedOffsetChanged(): void {
    // Our offsets are computed deterministically from measured heights, so
    // there is nothing for the strategy to reconcile after a render.
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this._viewport) return;
    const offset = this._offsetForIndex(index);
    this._lastWrittenScroll = offset;
    this._viewport.scrollToOffset(offset, behavior);
  }

  // Internals ---------------------------------------------------------------

  private _invalidateOffsets(): void {
    this._offsets = null;
  }

  private _ensureOffsets(): void {
    if (this._offsets) return;
    if (!this._viewport) {
      this._offsets = [0];
      return;
    }
    const len = this._viewport.getDataLength();
    const arr = new Array<number>(len + 1);
    arr[0] = 0;
    for (let i = 0; i < len; i++) {
      arr[i + 1] = arr[i] + this._heightFor(i);
    }
    this._offsets = arr;
  }

  private _heightFor(index: number): number {
    if (!this._itemIdProvider) return this._defaultHeight;
    const id = this._itemIdProvider.itemIdAt(index);
    if (id === undefined) return this._defaultHeight;
    const measured = this._heights.get(id);
    if (measured !== undefined) return measured;
    // No real measurement yet — use the host's per-row estimator if it's
    // wired up, otherwise the static default. The static default exists
    // only as a safety net; with the estimator the cascade adjustment on
    // first paint shrinks from "default 80 vs actual N" (often hundreds
    // of px wrong on tall rows) to "estimate vs actual" (typically < 10
    // px), small enough to hide inside the existing anchor preservation.
    return this._heightEstimator?.(index) ?? this._defaultHeight;
  }

  private _offsetForIndex(index: number): number {
    this._ensureOffsets();
    const arr = this._offsets!;
    return arr[Math.max(0, Math.min(index, arr.length - 1))];
  }

  // Binary search for the largest i such that _offsets[i] <= offset. That's
  // the index of the row that contains the given y-position.
  private _indexForOffset(offset: number): number {
    this._ensureOffsets();
    const arr = this._offsets!;
    if (arr.length <= 1) return 0;
    let lo = 0;
    let hi = arr.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if (arr[mid] <= offset) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  private _update(): void {
    if (!this._viewport) return;
    this._ensureOffsets();
    const total = this._offsets![this._offsets!.length - 1];
    this._viewport.setTotalContentSize(total);
    this._updateRenderedRange();
  }

  private _updateRenderedRange(): void {
    if (!this._viewport) return;
    this._ensureOffsets();

    const scrollOffset = this._viewport.measureScrollOffset();
    const viewportSize = this._viewport.getViewportSize();
    const dataLength = this._viewport.getDataLength();
    if (dataLength === 0) {
      this._viewport.setRenderedRange({ start: 0, end: 0 });
      this._viewport.setRenderedContentOffset(0);
      return;
    }

    const startOffset = Math.max(0, scrollOffset - this._bufferPx);
    const endOffset = scrollOffset + viewportSize + this._bufferPx;

    const start = this._indexForOffset(startOffset);
    const endIdx = this._indexForOffset(endOffset);
    const end = Math.min(dataLength, endIdx + 1);

    const range: ListRange = { start, end };
    const contentOffset = this._offsetForIndex(start);

    this._viewport.setRenderedRange(range);
    this._viewport.setRenderedContentOffset(contentOffset);
  }

  private _emitScrolledIndex(): void {
    if (!this._viewport) return;
    const scrollOffset = this._viewport.measureScrollOffset();
    this._scrolledIndexChange$.next(this._indexForOffset(scrollOffset));
  }
}

@Directive({
  selector: 'cdk-virtual-scroll-viewport[andorMessageListScroll]',
  exportAs: 'andorMessageListScroll',
  standalone: true,
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: (dir: AndorMessageListScrollDirective) => dir.strategy,
      deps: [forwardRef(() => AndorMessageListScrollDirective)],
    },
  ],
})
export class AndorMessageListScrollDirective implements OnChanges {
  // Fallback height for items that haven't been measured yet. A reasonable
  // default for our chat rows is ~80 px (one-line text msg with header);
  // tall rows (images) get corrected on the first ResizeObserver tick.
  @Input() defaultItemHeight = 80;
  @Input() bufferPx = 600;

  // Construction-time defaults match the field declarations above; the
  // template-bound values are pushed in via `ngOnChanges` (which fires for
  // the initial binding pass *before* `ngOnInit`).
  readonly strategy = new MessageListScrollStrategy(this.defaultItemHeight, this.bufferPx);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultItemHeight']) this.strategy.setDefaultHeight(this.defaultItemHeight);
    if (changes['bufferPx']) this.strategy.setBufferPx(this.bufferPx);
  }
}
